import { useEffect, useState } from 'react';
import { supabase } from '../lib/supabaseClient';

/**
 * Subscribe to delivery-status updates for a single outbound SMS.
 *
 * The webhook handler updates `communications.metadata` (jsonb) keyed by
 * `aircall_message_id` when Aircall fires `message.sent` and
 * `message.status_updated`. We filter the realtime channel to that exact
 * row so the bubble re-renders without a page refresh.
 *
 * Returns the latest metadata, falling back to whatever the bubble was
 * loaded with so the badge renders immediately on first paint.
 */
export function useSmsDeliveryStatus(
  aircallMessageId: string | undefined,
  initialMetadata?: Record<string, any>,
): Record<string, any> | undefined {
  const [metadata, setMetadata] = useState<Record<string, any> | undefined>(initialMetadata);

  // Keep state in sync if the parent's metadata updates from a refetch.
  useEffect(() => {
    setMetadata(initialMetadata);
  }, [initialMetadata]);

  useEffect(() => {
    if (!aircallMessageId) return;

    const channel = supabase
      .channel(`sms-status-${aircallMessageId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'communications',
          filter: `aircall_message_id=eq.${aircallMessageId}`,
        },
        (payload) => {
          const next = (payload.new as { metadata?: Record<string, any> } | null)?.metadata;
          if (next) setMetadata(next);
        },
      )
      .subscribe();

    return () => {
      void supabase.removeChannel(channel);
    };
  }, [aircallMessageId]);

  return metadata;
}
